// src/pages/LoginPage.tsx
import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Landmark, Zap, Bell, ShieldCheck, ListTodo, AlertTriangle, Mail, ShieldAlert, CheckCircle2, type LucideIcon } from 'lucide-react';
import { authService } from 'services/authService';
import { setCredentials } from 'store/slices/authSlice';
import FieldError from 'components/ui/FieldError';
import { isValidEmail } from 'utils/validators';

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from?.pathname || '/';

  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [mfaToken, setMfaToken]       = useState<string | null>(null);
  const [mfaCode, setMfaCode]         = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendStatus, setResendStatus] = useState('');
  const [resending, setResending]     = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [attempted, setAttempted]     = useState(false);
  const [mfaAttempted, setMfaAttempted] = useState(false);

  const fieldErrors: { email?: string; password?: string } = {};
  if (!email.trim()) fieldErrors.email = 'Email is required';
  else if (!isValidEmail(email)) fieldErrors.email = 'Enter a valid email address';
  if (!password) fieldErrors.password = 'Password is required';
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;

  function finishLogin(user: import('services/authService').User, accessToken: string) {
    dispatch(setCredentials({ user, accessToken }));
    // Admin users go to admin dashboard, others go to main app
    if (user.isAdmin) {
      navigate('/admin', { replace: true });
    } else {
      navigate(from, { replace: true });
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setAttempted(true);
    if (hasFieldErrors) return;
    setError('');
    setLoading(true);
    try {
      const res = await authService.login({ email, password });
      if ('emailVerificationRequired' in res) {
        setNeedsVerification(true);
      } else if ('mfaRequired' in res) {
        setMfaToken(res.mfaToken);
      } else {
        finishLogin(res.user, res.accessToken);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Login failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResending(true);
    setResendStatus('');
    try {
      await authService.resendVerification(email);
      setResendStatus('Verification email sent — check your inbox.');
    } catch (err: any) {
      setResendStatus(err?.response?.data?.message || 'Could not resend — try again shortly.');
    } finally {
      setResending(false);
    }
  }

  async function handleMfaSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMfaAttempted(true);
    if (!mfaCode.trim() || !mfaToken) return;
    setError('');
    setLoading(true);
    try {
      const res = await authService.verifyLoginMfa(mfaToken, mfaCode.trim());
      finishLogin(res.user, res.accessToken);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Invalid code.');
    } finally {
      setLoading(false);
    }
  }

  const inputCls = (hasError?: boolean) =>
    `w-full px-4 py-3.5 bg-[var(--om-card)] border rounded-xl ` +
    `text-[var(--om-text)] text-base outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 ` +
    `placeholder:text-[var(--om-text-dim)] transition-all ${hasError ? 'border-red-500' : 'border-[var(--om-border)]'}`;

  const benefits: { icon: LucideIcon; title: string; desc: string }[] = [
    { icon: Landmark, title: 'Role-based hierarchy', desc: 'Unlimited officer levels — Officer, PA, and Admin roles with configurable access.' },
    { icon: Zap, title: 'Smart priority engine', desc: 'Tasks are auto-prioritized server-side based on who assigned them to whom.' },
    { icon: Bell, title: 'Real-time notifications', desc: 'Instant updates, email alerts, and a full activity/audit trail on every task.' },
    { icon: ShieldCheck, title: 'Secured with JWT', desc: 'Access + refresh token auth, with optional MFA for extra protection.' },
  ];

  return (
    <div className="fixed inset-0 flex flex-col md:flex-row bg-[var(--om-app)]">

      {error && (
        <div className="flex items-center gap-1.5 fixed top-4 right-4 z-50 max-w-sm bg-red-500/10 border border-red-500/25 rounded-xl px-4 py-3 text-red-400 text-sm font-semibold shadow-2xl backdrop-blur-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" strokeWidth={2} /> {error}
        </div>
      )}

      {/* Left side — benefits */}
      <div className="hidden md:flex md:w-1/2 flex-col justify-center px-12 lg:px-16 bg-gradient-to-br from-blue-900 via-[#0d1830] to-[var(--om-app)] text-white relative overflow-hidden">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-800 to-blue-500 flex items-center justify-center mb-6 shadow-xl shadow-blue-500/30">
          <ListTodo className="w-7 h-7" strokeWidth={2} />
        </div>
        <h1 className="text-3xl font-black tracking-wide">TODO</h1>
        <p className="text-[13px] text-blue-200/70 mt-1 mb-10">Task Management System</p>

        <div className="space-y-6 max-w-md">
          {benefits.map(b => (
            <div key={b.title} className="flex gap-4">
              <div className="shrink-0"><b.icon className="w-6 h-6" strokeWidth={2} /></div>
              <div>
                <p className="font-bold text-sm">{b.title}</p>
                <p className="text-[13px] text-blue-200/60 mt-0.5">{b.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Demo hint */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-3 mt-10 max-w-md text-[11px] space-y-1">
          <p className="text-green-400 font-bold mb-1">Demo Credentials</p>
          <p className="inline-flex items-center gap-1 text-blue-200/70"><Landmark className="w-3 h-3" strokeWidth={2} /> Officer: <span className="text-blue-300">officer@om.app</span> / <span className="text-blue-300">password123</span></p>
          <p className="inline-flex items-center gap-1 text-blue-200/70"><ListTodo className="w-3 h-3" strokeWidth={2} /> PA: <span className="text-blue-300">pa@om.app</span> / <span className="text-blue-300">password123</span></p>
          <p className="inline-flex items-center gap-1 text-blue-200/70"><ShieldCheck className="w-3 h-3" strokeWidth={2} /> Admin: <span className="text-blue-300">admin@om.app</span> / <span className="text-blue-300">password123</span></p>
        </div>
      </div>

      {/* Right side — form */}
      <div className="flex-1 flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-[var(--om-panel)] border border-[var(--om-border)] rounded-3xl p-10 lg:p-12 shadow-2xl">

        {/* Brand */}
        <div className="flex items-center justify-start gap-4 mb-8">
          <div className="w-16 h-16 shrink-0 rounded-2xl bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/25 ring-4 ring-blue-500/10 animate-bounce [animation-duration:2.5s]">
            <ListTodo className="w-7 h-7 text-white" strokeWidth={2} />
          </div>
          <div className="text-left">
            <h1 className="text-2xl font-black text-[var(--om-text)] tracking-tight">TODO</h1>
            <p className="flex items-center gap-1.5 text-sm text-[var(--om-text-dim)] mt-1">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
              Task Management System
            </p>
          </div>
        </div>

        {needsVerification ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-1.5 bg-[var(--om-card)] border border-[var(--om-border)] rounded-xl p-4 text-sm text-[var(--om-text-muted)] text-center">
              <Mail className="w-4 h-4 flex-shrink-0" strokeWidth={2} /> Please verify your email address before signing in. Check your inbox for the verification link.
            </div>

            {resendStatus && (
              <div className="bg-blue-500/10 border border-blue-500/25 rounded-xl px-4 py-3 text-blue-400 text-sm text-center">
                {resendStatus}
              </div>
            )}

            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="inline-flex items-center justify-center gap-1.5 w-full py-3.5 bg-blue-600 hover:bg-blue-500 disabled:bg-[var(--om-border)] disabled:text-[var(--om-text-dim)] text-white font-bold text-base rounded-xl transition-all"
            >
              {resending ? 'Sending...' : <><Mail className="w-4 h-4" strokeWidth={2} /> Resend Verification Email</>}
            </button>

            <button
              type="button"
              onClick={() => { setNeedsVerification(false); setResendStatus(''); setError(''); }}
              className="w-full text-sm text-[var(--om-text-dim)] hover:text-[var(--om-text-muted)] text-center"
            >
              ← Back to login
            </button>
          </div>
        ) : mfaToken ? (
          <form onSubmit={handleMfaSubmit} noValidate className="space-y-4">
            <div className="flex items-center justify-center gap-1.5 bg-[var(--om-card)] border border-[var(--om-border)] rounded-xl p-4 text-sm text-[var(--om-text-muted)] text-center">
              <ShieldAlert className="w-4 h-4 flex-shrink-0" strokeWidth={2} /> Enter the 6-digit code from your authenticator app (or an 8-character backup code)
            </div>

            <div>
              <label className="text-sm text-[var(--om-text-muted)] font-semibold block mb-1.5">Authentication Code</label>
              <input
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value)}
                placeholder="123456"
                autoComplete="one-time-code"
                autoFocus
                className={`${inputCls(mfaAttempted && !mfaCode.trim())} text-center tracking-[0.3em] font-mono`}
              />
              <FieldError message={mfaAttempted && !mfaCode.trim() ? 'Enter the code' : undefined} />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`inline-flex items-center justify-center gap-1.5 w-full py-3.5 bg-blue-600 hover:bg-blue-500 disabled:bg-[var(--om-border)] disabled:text-[var(--om-text-dim)] text-white font-bold text-base rounded-xl transition-all ${
                mfaAttempted && !mfaCode.trim() ? 'opacity-70' : ''
              }`}
            >
              {loading ? 'Verifying...' : <><CheckCircle2 className="w-4 h-4" strokeWidth={2} /> Verify</>}
            </button>

            <button
              type="button"
              onClick={() => { setMfaToken(null); setMfaCode(''); setError(''); }}
              className="w-full text-sm text-[var(--om-text-dim)] hover:text-[var(--om-text-muted)] text-center"
            >
              ← Back to login
            </button>
          </form>
        ) : (
        <>
        <form onSubmit={handleSubmit} noValidate className="space-y-5">

          {/* Email */}
          <div>
            <label className="text-sm text-[var(--om-text-muted)] font-semibold block mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Enter your email"
              autoComplete="email"
              className={inputCls(attempted && !!fieldErrors.email)}
            />
            <FieldError message={attempted ? fieldErrors.email : undefined} />
          </div>

          {/* Password */}
          <div>
            <label className="text-sm text-[var(--om-text-muted)] font-semibold block mb-1.5">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                className={`${inputCls(attempted && !!fieldErrors.password)} pr-12`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--om-text-dim)] hover:text-[var(--om-text-muted)] p-1"
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.5 18.5 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            <FieldError message={attempted ? fieldErrors.password : undefined} />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-1.5 w-full py-3.5 bg-blue-600 hover:bg-blue-500 disabled:bg-[var(--om-border)] disabled:text-[var(--om-text-dim)] text-white font-bold text-base rounded-xl transition-all shadow-lg shadow-blue-500/20"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
                Signing in...
              </span>
            ) : <><ShieldCheck className="w-4 h-4" strokeWidth={2} /> Sign In</>}
          </button>

        </form>

        <p className="text-center text-sm text-[var(--om-text-dim)] mt-6">
          New organization? <Link to="/register" className="text-blue-400 hover:text-blue-300 font-semibold">Register here</Link>
        </p>
        </>
        )}
      </div>
      </div>
    </div>
  );
}
